// 定义API响应类型
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Company {
  id: number;
  tenant_code: string;
  company_name: string;
  database_name: string;
  project_id?: string | number | null;
  description?: string;
  email?: string;
  whatsapp?: string;
  mobile?: string;
  alias?: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCompanyRequest {
  company_name: string;
  database_name: string;
  project_id?: string | number | null;
  description?: string;
  email?: string;
  whatsapp?: string;
  mobile?: string;
  alias?: string;
  is_active?: number;
}

export interface UpdateCompanyRequest {
  company_name?: string;
  database_name?: string;
  project_id?: string | number | null;
  description?: string;
  email?: string;
  whatsapp?: string;
  mobile?: string;
  alias?: string;
  is_active?: number;
}

/**
 * 按 project_id 去重聚合公司名，用于下拉：value=project_id，label=公司名称（多公司同 project 时逗号拼接）
 */
export function buildProjectIdSelectOptions(
  companies: Company[],
  allLabel: string
): { value: string; label: string }[] {
  const list = companies.filter(
    (c) => c.project_id != null && String(c.project_id).trim() !== ''
  );
  const byPid = new Map<string, string[]>();
  for (const c of list) {
    const pid = String(c.project_id);
    if (!byPid.has(pid)) byPid.set(pid, []);
    if (c.company_name) byPid.get(pid)!.push(c.company_name);
  }
  const options = Array.from(byPid.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([pid, names]) => ({
      value: pid,
      label: names.length ? names.join(', ') : pid,
    }));
  return [{ value: 'all', label: allLabel }, ...options];
}

class CompanyService {
  private baseUrl = '/api';

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem('token');
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * 获取租户下的所有公司
   */
  async getCompanies(tenantCode: string): Promise<ApiResponse<Company[]>> {
    return this.request<Company[]>(`/${tenantCode}/companies`);
  }

  /**
   * 根据ID获取公司信息
   */
  async getCompanyById(tenantCode: string, id: number): Promise<ApiResponse<Company>> {
    return this.request<Company>(`/${tenantCode}/companies/${id}`);
  }

  /**
   * 创建新公司
   */
  async createCompany(tenantCode: string, request: CreateCompanyRequest): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>(`/${tenantCode}/companies`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * 更新公司信息
   */
  async updateCompany(tenantCode: string, id: number, request: UpdateCompanyRequest): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/${tenantCode}/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /**
   * 根据数据库名称查找公司
   */
  async findCompanyByDatabaseName(tenantCode: string, databaseName: string): Promise<ApiResponse<Company>> {
    return this.request<Company>(`/${tenantCode}/companies/database/${encodeURIComponent(databaseName)}`);
  }
}

export const companyService = new CompanyService();
